import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useData } from "../context/DataContext";
import { GraduationCap, BookOpen, CreditCard, Receipt, AlertCircle, CheckCircle2, CalendarCheck, XCircle, Clock4, FileMinus2, PlusCircle, Sparkles, Calendar as CalendarIcon, Users, X, Loader2 } from "lucide-react";

const STATUS = {
  pending: { label: "En attente", color: "bg-amber-100 text-amber-800", icon: AlertCircle },
  paid: { label: "Payé", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  partial: { label: "Partiel", color: "bg-sky-100 text-sky-800", icon: AlertCircle },
  failed: { label: "Échec", color: "bg-red-100 text-red-700", icon: AlertCircle },
};
const MODES = { cheque: "Chèque", especes: "Espèces", card: "Carte bancaire", monthly: "Prélèvement mensuel", pass_plus: "Pass+ / Pass Région" };
const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

const ParentDashboard = () => {
  const { stages = [], settings } = useData();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/parent/me").then(({ data }) => setData(data)).catch(() => toast.error("Erreur")).finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  if (loading) return <div className="text-stone-500 text-center py-12">Chargement...</div>;
  if (!data) return <div className="text-stone-500 text-center py-12">Aucune donnée.</div>;

  const { family, students, enrollments, payments, attendances = [], orders = [], summary = {} } = data;
  const total = Number(summary.totalFinal || 0);
  const paid = Number(summary.totalPaid || 0);
  const remaining = Number(summary.remainingAmount || 0);

  const ATT_META = {
    present: { label: "Présent", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
    absent: { label: "Absent", color: "bg-red-100 text-red-700", icon: XCircle },
    late: { label: "Retard", color: "bg-amber-100 text-amber-800", icon: Clock4 },
    excused: { label: "Excusé", color: "bg-sky-100 text-sky-700", icon: FileMinus2 },
  };

  return (
    <>
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl text-stone-800">Bonjour {family?.parentName?.split(" ")[0]} 👋</h1>
          <p className="text-stone-500 mt-1">Bienvenue dans votre espace famille.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <SummaryCard label="Élèves" value={students.length} icon={GraduationCap} color="bg-blush-light text-deep-rose" />
        <SummaryCard label="Inscriptions" value={enrollments.length} icon={BookOpen} color="bg-sky-soft text-sky-700" />
        <SummaryCard label="Total commandé" value={fmtEur(total)} icon={Receipt} color="bg-stone-100 text-stone-700" />
        <SummaryCard label="Reste à payer" value={fmtEur(remaining)} icon={CreditCard} color={remaining > 0.01 ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700"} />
      </div>

      {orders.length > 0 && (
        <section>
          <h2 className="font-serif-display text-xl text-stone-800 mb-3 flex items-center gap-2"><Receipt size={18} className="text-deep-rose"/> Mes commandes</h2>
          <div className="space-y-2" data-testid="parent-orders-list">
            {orders.map((o) => {
              const rest = Number(o.remainingAmount || 0);
              const statusColor = o.paymentStatus === "paid" ? "bg-emerald-100 text-emerald-700"
                : o.paymentStatus === "partial" ? "bg-amber-100 text-amber-800"
                : "bg-stone-100 text-stone-600";
              const statusLabel = o.paymentStatus === "paid" ? "Payée" : o.paymentStatus === "partial" ? "Partielle" : "En attente";
              return (
                <div key={o.id} data-testid={`parent-order-${o.id}`} className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-stone-500">{o.orderNumber || o.id?.slice(0, 8)}</div>
                    <div className="text-stone-800 text-sm mt-0.5">
                      {new Date(o.createdAt).toLocaleDateString("fr-FR")} · {o.paymentMode}
                      {o.type === "stage" && <span className="ml-2 text-[10px] uppercase tracking-widest bg-blush-light text-deep-rose px-2 py-0.5 rounded-full">Stage</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif-display text-stone-800">{fmtEur(o.totalFinal)}</div>
                    <div className="text-xs text-stone-500">payé : <span className="text-emerald-700 font-medium">{fmtEur(o.totalPaid)}</span></div>
                    {rest > 0.01 && (
                      <div className="text-xs text-amber-700 font-medium">reste : {fmtEur(rest)}</div>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-serif-display text-xl text-stone-800 mb-3">Mes élèves</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {students.length === 0 && <div className="col-span-full bg-white rounded-xl p-6 text-stone-500 text-sm text-center">Aucun élève enregistré.</div>}
          {students.map((s) => (
            <div key={s.id} className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blush-light flex items-center justify-center text-deep-rose shrink-0"><GraduationCap size={20}/></div>
              <div>
                <div className="font-serif-display text-stone-800">{s.firstName} {s.lastName}</div>
                <div className="text-stone-500 text-xs">{s.age ? `${s.age} ans` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif-display text-xl text-stone-800 mb-3">Mes inscriptions</h2>
        <div className="space-y-2">
          {enrollments.length === 0 && <div className="bg-white rounded-xl p-6 text-stone-500 text-sm text-center">Aucune inscription pour le moment.</div>}
          {enrollments.map((e) => {
            const st = STATUS[e.paymentStatus] || STATUS.pending;
            const Icon = st.icon;
            return (
              <div key={e.id} className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-serif-display text-stone-800">
                      <span className="text-stone-500 mr-1">{e.studentFirstName} —</span>{e.courseName}
                    </div>
                    <div className="text-stone-500 text-xs mt-1">Saison {e.season} · {MODES[e.paymentMode]}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif-display text-stone-800">{fmtEur(e.finalAmount)}</div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs mt-1 ${st.color}`}>
                      <Icon size={12}/> {st.label}
                    </span>
                  </div>
                </div>
                {e.paidAmount > 0 && e.paymentStatus !== "paid" && (
                  <div className="mt-2 text-xs text-stone-500">Déjà versé : {fmtEur(e.paidAmount)} / {fmtEur(e.finalAmount)}</div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif-display text-xl text-stone-800 flex items-center gap-2"><Sparkles size={18} className="text-deep-rose"/> Stages de vacances</h2>
        </div>
        {stages.length === 0 ? (
          <div className="bg-white rounded-xl p-6 text-stone-500 text-sm text-center">Aucun stage n&apos;est programmé pour le moment.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {stages.map((s) => (
              <div key={s.id} data-testid={`parent-stage-card-${s.id}`} className="bg-white rounded-2xl shadow-sm p-4 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-serif-display text-stone-800 truncate">{s.title}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-500 mt-1">
                      {s.dates && <span className="inline-flex items-center gap-1"><CalendarIcon size={12}/>{s.dates}</span>}
                      {s.ageRange && <span>{s.ageRange}</span>}
                      {s.maxStudents > 0 && <span className="inline-flex items-center gap-1"><Users size={12}/>{s.maxStudents}</span>}
                    </div>
                  </div>
                  <span className="font-serif-display text-lg text-deep-rose whitespace-nowrap">{fmtEur(s.price)}</span>
                </div>
                {s.description && <p className="text-stone-600 text-xs mt-2 line-clamp-2">{s.description}</p>}
                <button
                  onClick={() => setSelectedStage(s)}
                  data-testid={`parent-stage-book-btn-${s.id}`}
                  className="mt-3 self-start inline-flex items-center gap-1 bg-coral text-white px-4 py-1.5 rounded-full text-xs tracking-widest uppercase hover:brightness-105 transition"
                >
                  <PlusCircle size={12}/> Réserver
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-serif-display text-xl text-stone-800 mb-3 flex items-center gap-2"><CalendarCheck size={18} className="text-deep-rose"/> Présences récentes</h2>
        <div className="space-y-2">
          {attendances.length === 0 && <div className="bg-white rounded-xl p-6 text-stone-500 text-sm text-center">Aucune présence enregistrée pour le moment.</div>}
          {attendances.slice(0, 20).map((a) => {
            const meta = ATT_META[a.status] || ATT_META.present;
            const Icon = meta.icon;
            return (
              <div key={a.id} className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-serif-display text-stone-800 text-sm">
                    <span className="text-stone-500 mr-1">{a.studentFirstName} —</span>{a.courseName}
                  </div>
                  <div className="text-stone-500 text-xs">{fmtDate(a.date)}{a.notes ? ` · ${a.notes}` : ""}</div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${meta.color}`}>
                  <Icon size={12}/> {meta.label}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="font-serif-display text-xl text-stone-800 mb-3">Mes paiements en ligne</h2>
        <div className="space-y-2">
          {payments.length === 0 && <div className="bg-white rounded-xl p-6 text-stone-500 text-sm text-center">Aucun paiement en ligne.</div>}
          {payments.map((p) => (
            <div key={p.id} className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Receipt size={18}/></div>
              <div className="min-w-0 flex-1">
                <div className="font-serif-display text-stone-800">{fmtEur(p.amount)}</div>
                <div className="text-stone-500 text-xs">{fmtDate(p.createdAt)} · {MODES[p.mode] || p.mode}</div>
              </div>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${p.paymentStatus === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {p.paymentStatus === "paid" ? "Payé" : "En attente"}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
    {selectedStage && (
      <StageBookingModal
        stage={selectedStage}
        students={students}
        applicationFee={Number(settings?.applicationFee || 0)}
        onClose={() => setSelectedStage(null)}
        onBooked={() => { setSelectedStage(null); reload(); }}
      />
    )}
    </>
  );
};

const StageBookingModal = ({ stage, students = [], applicationFee, onClose, onBooked }) => {
  const [mode, setMode] = useState(students.length > 0 ? "existing" : "new");
  const [studentId, setStudentId] = useState(students[0]?.id || "");
  const [newStudent, setNewStudent] = useState({ firstName: "", lastName: "", birthDate: "" });
  const [paymentMode, setPaymentMode] = useState("cheque");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const total = Number(stage?.price || 0) + Number(applicationFee || 0);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { stageId: stage.id, paymentMode, message };
    if (mode === "existing") {
      if (!studentId) { toast.error("Sélectionnez un élève."); return; }
      payload.studentId = studentId;
    } else {
      if (!newStudent.firstName?.trim() || !newStudent.lastName?.trim() || !newStudent.birthDate) {
        toast.error("Renseignez prénom, nom et date de naissance.");
        return;
      }
      payload.newStudent = newStudent;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/parent/stages/book", payload);
      if (data.requiresPayment) {
        try {
          const { data: chk } = await api.post("/payments/checkout", {
            enrollmentIds: data.enrollmentIds,
            originUrl: window.location.origin,
            mode: paymentMode,
          });
          try {
            sessionStorage.setItem("elan_stripe_retry", JSON.stringify({
              enrollmentIds: data.enrollmentIds,
              orderNumber: data.orderNumber || "",
              orderId: data.orderId || "",
              mode: paymentMode,
              total: data.total,
              savedAt: Date.now(),
              returnTo: "/espace-famille",
            }));
          } catch { /* ignore */ }
          window.location.href = chk.url;
          return;
        } catch (payErr) {
          toast.error(payErr?.response?.data?.detail || "Erreur paiement — la réservation est enregistrée, nous vous contacterons.");
        }
      }
      toast.success("Stage réservé !");
      onBooked?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="parent-stage-modal" className="bg-white rounded-3xl shadow-xl max-w-lg w-full my-8 p-6 sm:p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600"><X size={18}/></button>
        <div className="text-xs uppercase tracking-widest text-deep-rose">Réservation de stage</div>
        <h3 className="font-serif-display text-2xl text-stone-800 mt-1">{stage.title}</h3>
        <p className="text-stone-500 text-sm mt-1">{stage.dates}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="bg-blush-light text-deep-rose px-3 py-1 rounded-full">{fmtEur(stage.price)}</span>
          {applicationFee > 0 && <span className="bg-stone-100 text-stone-600 px-3 py-1 rounded-full">+ frais dossier {fmtEur(applicationFee)}</span>}
          <span className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">Total : {fmtEur(total)}</span>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Élève inscrit au stage</div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setMode("existing")} disabled={students.length === 0} data-testid="parent-stage-mode-existing"
                className={`rounded-xl border px-3 py-2 text-sm transition ${mode === "existing" ? "border-deep-rose bg-blush-light/50 text-deep-rose font-medium" : "border-stone-200 bg-white hover:bg-stone-50 text-stone-700"} ${students.length === 0 ? "opacity-40 cursor-not-allowed" : ""}`}>
                Élève existant
              </button>
              <button type="button" onClick={() => setMode("new")} data-testid="parent-stage-mode-new"
                className={`rounded-xl border px-3 py-2 text-sm transition ${mode === "new" ? "border-deep-rose bg-blush-light/50 text-deep-rose font-medium" : "border-stone-200 bg-white hover:bg-stone-50 text-stone-700"}`}>
                Nouvel enfant
              </button>
            </div>
          </div>

          {mode === "existing" ? (
            <select value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="parent-stage-student-select"
              className="w-full bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush">
              {students.length === 0 && <option value="">Aucun élève enregistré</option>}
              {students.map((s) => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
            </select>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input required placeholder="Prénom *" value={newStudent.firstName} onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })} data-testid="parent-stage-new-first"
                className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
              <input required placeholder="Nom *" value={newStudent.lastName} onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })} data-testid="parent-stage-new-last"
                className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
              <div className="flex flex-col">
                <label className="text-[10px] uppercase tracking-widest text-stone-500 mb-0.5">Naissance *</label>
                <input required type="date" value={newStudent.birthDate} onChange={(e) => setNewStudent({ ...newStudent, birthDate: e.target.value })} data-testid="parent-stage-new-birth"
                  className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
              </div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Mode de paiement</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "cheque", label: "Chèque" },
                { key: "especes", label: "Espèces" },
                { key: "card", label: "Carte bancaire" },
                { key: "monthly", label: "Prélèvement 8x (SEPA)" },
              ].map(({ key, label }) => (
                <button type="button" key={key} onClick={() => setPaymentMode(key)} data-testid={`parent-stage-pay-${key}`}
                  className={`rounded-xl border px-3 py-2 text-sm transition ${paymentMode === key ? "border-deep-rose bg-blush-light/50 text-deep-rose font-medium" : "border-stone-200 bg-white hover:bg-stone-50 text-stone-700"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <textarea rows={2} placeholder="Message (optionnel)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="parent-stage-message"
            className="w-full bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>

          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button type="button" onClick={onClose} className="px-5 py-2 rounded-full bg-stone-100 text-stone-700 text-sm">Annuler</button>
            <button type="submit" disabled={submitting} data-testid="parent-stage-submit"
              className="px-6 py-2 rounded-full bg-coral text-white text-sm tracking-widest uppercase disabled:opacity-50 inline-flex items-center gap-2">
              {submitting ? <><Loader2 className="animate-spin" size={14}/> Envoi...</> : (paymentMode === "card" || paymentMode === "monthly") ? "Payer & réserver" : "Réserver"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl shadow-sm p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}><Icon size={22} /></div>
    <div>
      <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-2xl font-serif-display text-stone-800">{value}</div>
    </div>
  </div>
);

export default ParentDashboard;
